/**
 * ShareCard — canvas-based renderer for a shareable Lexia School progress card.
 *
 * Renders a 1200×630 PNG in memory (Open Graph aspect ratio + high enough
 * resolution for Twitter/LinkedIn) and exposes helpers to download or copy
 * the image. Colors, typography and layout match Lexia's editorial look
 * (paper, ink, terracotta accent + serif hierarchy).
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Download, Copy, Share2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const WIDTH = 1200;
const HEIGHT = 630;

const COLORS = {
    paper: "#F5EFE6",
    paperAlt: "#EFE8DE",
    ink: "#1E1E1E",
    inkSoft: "#5C574F",
    terracotta: "#C1552B",
    emerald: "#2D6A4F",
    border: "#D8CDBE",
};

function drawWrappedText(ctx, text, x, y, maxWidth, lineHeight) {
    const words = text.split(" ");
    let line = "";
    let ny = y;
    for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (ctx.measureText(test).width > maxWidth && line) {
            ctx.fillText(line, x, ny);
            line = w;
            ny += lineHeight;
        } else {
            line = test;
        }
    }
    if (line) ctx.fillText(line, x, ny);
    return ny;
}

function renderShareCard(canvas, snapshot) {
    const ctx = canvas.getContext("2d");
    canvas.width = WIDTH;
    canvas.height = HEIGHT;

    // Background — solid paper.
    ctx.fillStyle = COLORS.paper;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    // Left band — deep ink (editorial cover feel).
    ctx.fillStyle = COLORS.ink;
    ctx.fillRect(0, 0, 440, HEIGHT);

    // Subtle terracotta ribbon on the left band.
    ctx.fillStyle = COLORS.terracotta;
    ctx.fillRect(72, 78, 8, 74);

    // Brand + eyebrow (ink band).
    ctx.fillStyle = "#F5EFE6";
    ctx.font = "600 30px 'Times New Roman', serif";
    ctx.fillText("Lexia School", 72, 200);
    ctx.font = "500 15px 'Helvetica Neue', sans-serif";
    ctx.fillStyle = "#B8AFA0";
    const eyebrow = "PROGRESSION · " + (snapshot.target_language_fr || "").toUpperCase();
    ctx.fillText(eyebrow, 72, 240);

    // Learner name.
    ctx.font = "700 56px 'Times New Roman', serif";
    ctx.fillStyle = "#F5EFE6";
    ctx.fillText(snapshot.first_name || "Apprenante", 72, 340);

    // Level + variant.
    ctx.font = "500 22px 'Helvetica Neue', sans-serif";
    ctx.fillStyle = "#D8CDBE";
    const variant = snapshot.variant ? ` (${snapshot.variant})` : "";
    ctx.fillText(`Niveau ${snapshot.level}${variant}`, 72, 378);

    // Right block — big counters on paper.
    // Two rows: mastered (top), streak + active (bottom).
    ctx.fillStyle = COLORS.ink;
    ctx.font = "700 128px 'Times New Roman', serif";
    ctx.fillText(String(snapshot.words_mastered ?? 0), 520, 260);
    ctx.font = "500 22px 'Helvetica Neue', sans-serif";
    ctx.fillStyle = COLORS.inkSoft;
    ctx.fillText("mots maîtrisés", 520, 292);

    // Divider.
    ctx.strokeStyle = COLORS.border;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(520, 340);
    ctx.lineTo(WIDTH - 72, 340);
    ctx.stroke();

    // Bottom two counters.
    ctx.fillStyle = COLORS.terracotta;
    ctx.font = "700 72px 'Times New Roman', serif";
    ctx.fillText(String(snapshot.streak_days ?? 0), 520, 440);
    ctx.font = "500 18px 'Helvetica Neue', sans-serif";
    ctx.fillStyle = COLORS.inkSoft;
    ctx.fillText("jours de série", 520, 470);

    ctx.fillStyle = COLORS.emerald;
    ctx.font = "700 72px 'Times New Roman', serif";
    ctx.fillText(String(snapshot.words_active ?? 0), 800, 440);
    ctx.font = "500 18px 'Helvetica Neue', sans-serif";
    ctx.fillStyle = COLORS.inkSoft;
    ctx.fillText("mots actifs", 800, 470);

    // Footer tagline on paper side.
    ctx.fillStyle = COLORS.inkSoft;
    ctx.font = "italic 500 18px 'Times New Roman', serif";
    drawWrappedText(
        ctx,
        "La régularité vaut plus que le volume — apprendre une langue, un mot à la fois.",
        520,
        548,
        WIDTH - 520 - 72,
        26,
    );

    // Ink band footer.
    ctx.fillStyle = "#B8AFA0";
    ctx.font = "500 14px 'Helvetica Neue', sans-serif";
    ctx.fillText("lexia.school", 72, HEIGHT - 60);
}

const ShareCard = forwardRef(function ShareCard({ snapshot, publicUrl }, ref) {
    const canvasRef = useRef(null);
    const [copying, setCopying] = useState(false);
    const [copied, setCopied] = useState(false);

    const rerender = useCallback(() => {
        if (canvasRef.current && snapshot) {
            renderShareCard(canvasRef.current, snapshot);
        }
    }, [snapshot]);

    useEffect(() => {
        rerender();
    }, [rerender]);

    useImperativeHandle(ref, () => ({
        download() {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const dataUrl = canvas.toDataURL("image/png");
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = `lexia-school-${(snapshot.first_name || "progression").toLowerCase()}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
        async copyLink() {
            if (!publicUrl) return;
            try {
                await navigator.clipboard.writeText(publicUrl);
                toast.success("Lien copié dans le presse-papiers.");
            } catch {
                toast.error("Impossible de copier le lien.");
            }
        },
    }));

    async function handleDownload() {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dataUrl = canvas.toDataURL("image/png");
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `lexia-school-${(snapshot.first_name || "progression").toLowerCase()}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    async function handleCopyLink() {
        if (!publicUrl) return;
        setCopying(true);
        try {
            await navigator.clipboard.writeText(publicUrl);
            setCopied(true);
            toast.success("Lien copié dans le presse-papiers.");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Impossible de copier le lien.");
        } finally {
            setCopying(false);
        }
    }

    async function handleWebShare() {
        if (!navigator.share) {
            toast.info("Partage direct non disponible sur ce navigateur — copiez le lien.");
            return;
        }
        try {
            await navigator.share({
                title: "Ma progression Lexia School",
                text: `${snapshot.first_name} — ${snapshot.words_mastered} mots maîtrisés · série de ${snapshot.streak_days} jours en ${snapshot.target_language_fr}.`,
                url: publicUrl,
            });
        } catch {
            // User cancelled — silent.
        }
    }

    return (
        <div data-testid="share-card">
            <div className="rounded-2xl border border-lexiBorder overflow-hidden bg-paper shadow-sm">
                <canvas
                    ref={canvasRef}
                    className="w-full h-auto block"
                    data-testid="share-card-canvas"
                />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
                <button
                    onClick={handleDownload}
                    data-testid="share-card-download"
                    className="inline-flex items-center gap-2 bg-ink text-white hover:bg-emerald rounded-full px-4 py-2 text-sm transition-colors"
                >
                    <Download className="w-4 h-4" /> Télécharger le PNG
                </button>
                {publicUrl && (
                    <button
                        onClick={handleCopyLink}
                        disabled={copying}
                        data-testid="share-card-copy-link"
                        className="inline-flex items-center gap-2 bg-paperAlt border border-lexiBorder hover:bg-paper rounded-full px-4 py-2 text-sm transition-colors"
                    >
                        {copying ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : copied ? (
                            <Check className="w-4 h-4 text-emerald" />
                        ) : (
                            <Copy className="w-4 h-4" />
                        )}
                        {copied ? "Lien copié" : "Copier le lien"}
                    </button>
                )}
                {publicUrl && (
                    <button
                        onClick={handleWebShare}
                        data-testid="share-card-webshare"
                        className="inline-flex items-center gap-2 bg-terracotta text-white hover:bg-terracottaDark rounded-full px-4 py-2 text-sm transition-colors"
                    >
                        <Share2 className="w-4 h-4" /> Partager
                    </button>
                )}
            </div>
        </div>
    );
});

export default ShareCard;
